import { ref, Ref, useRoute, computed } from '@nuxtjs/composition-api';
import {
  Blog,
  useGetBlogsQuery,
  useGetBlogCountQuery,
} from '@/lib/gql/core.gql.generated';
import { BuilderClient } from '@/modules/apollo/const';
import { useApolloQuery, useWrapperClient } from '@/hooks/useApolloWrapper';
import { FETCH_POLICY } from '@/modules/apollo/builder.apollo';

let isInitialized: boolean;
let route;

const blogs: Ref<Blog[]> = ref([]);
const referralBlogs: Ref<Blog[]> = ref([]);
const totalBlogCount: Ref<number> = ref(0);
const isLoading: Ref<boolean> = ref(false);

// offset is the utility Builder.io exposes to paginate content
const fetchBlogs = (
  offset: number = 0,
  limit: number = 7,
  isReferral: boolean = false,
  force: boolean = false
) => {
  useWrapperClient(BuilderClient);
  if (force) {
    blogs.value = [];
  }

  const { onResult, onError } = useApolloQuery(
    useGetBlogsQuery,
    {
      offset,
      limit,
    },
    {
      pending: isLoading,
      data: isReferral ? referralBlogs : blogs,
    },
    {
      placeholderPick: ({ blog: res }) =>
        isReferral ? res : [...blogs.value, ...res],
    },
    {
      fetchPolicy: force
        ? FETCH_POLICY.NETWORK_ONLY
        : FETCH_POLICY.CACHE_AND_NETWORK,
    }
  );
  onError((err) => {
    // @ts-ignore
    window?.DD_RUM?.addError(new Error('Error fetching blogs', { err }));
  });

  onResult(() => {
    // Do not include the current blog in the referral list
    if (referralBlogs.value) {
      referralBlogs.value = referralBlogs.value.filter((referralBlog) => {
        return referralBlog.data?.slug !== route?.value?.path;
      });
    }
  });

  return {
    blogs,
    referralBlogs,
    isLoading,
  };
};

// fetch the count of all blogs
const fetchBlogCount = () => {
  useWrapperClient(BuilderClient);
  const { onResult } = useApolloQuery(
    // @ts-ignore
    useGetBlogCountQuery,
    {},
    {
      data: totalBlogCount,
    },
    {},
    {
      fetchPolicy: FETCH_POLICY.NETWORK_ONLY,
    }
  );

  onResult((res) => {
    if (res?.blog) {
      totalBlogCount.value = res.blog.length;
    }
  });
};

const hasShownAllBlogs = computed(
  () => totalBlogCount.value === blogs.value.length
);

const setupHook = () => {
  route = useRoute();
};

const useBlogs = () => {
  if (!isInitialized) {
    setupHook();
    isInitialized = true;
  }

  return {
    // Data
    hasShownAllBlogs,
    blogs,
    referralBlogs,
    // Methods
    fetchBlogs,
    fetchBlogCount,
  };
};

export default useBlogs;
